"use client";

import React from "react";
import { ClipboardCheck, Flame, Wind, Hammer, PhoneCall, ArrowRight, ShieldAlert, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface ProcessStep {
  step: string;
  phase: string;
  title: string;
  summary: string;
  bullets: string[];
  specs: string;
}
const steps: ProcessStep[] = [{
  step: "01",
  phase: "Phase 1: Immediate Safety",
  title: "Damage assessment",
  summary: "We inspect structural integrity, seal broken windows or roof breaches, and document all loss for your adjuster.",
  bullets: ["Structural load & framing stability check", "Immediate board-up & roof tarping", "Itemized insurance scoping & photo logs"],
  specs: "Response: 60-90 min dispatch"
}, {
  step: "02",
  phase: "Phase 2: Mitigation",
  title: "Soot & debris removal",
  summary: "Charred building elements and acidic residue are cleared before they etch into plumbing fixtures, masonry, and metals.",
  bullets: ["Bulk demolition of unsalvageable drywall", "HEPA vacuuming & dry chemical sponging", "Corrosive soot neutralization on metals"],
  specs: "Filtration: 99.97% HEPA capture"
}, {
  step: "03",
  phase: "Phase 3: Neutralization",
  title: "Smoke odor treatment",
  summary: "Burnt protein and synthetic smoke particles are neutralized deep inside wall cavities, subflooring, and ductwork.",
  bullets: ["Thermal fogging penetrates porous framing", "Ozone treatment & hydroxyl air scrubbing", "Antimicrobial air-duct decontamination"],
  specs: "Air quality: 0% residual VOCs"
}, {
  step: "04",
  phase: "Phase 4: Turn-Key Finish",
  title: "Reconstruction & repair",
  summary: "Our licensed in-house rebuild crews repair joists, install fresh electrical wiring, and return your property to pre-loss state.",
  bullets: ["Rough carpentry, framing & structural repair", "Licensed electrical & plumbing pull-through", "Drywall hanging, trim & paint matching"],
  specs: "Turn-key: Single contractor lead"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (index: number) => {
    switch (index) {
      case 0:
        return <ClipboardCheck className="size-5 text-primary" aria-hidden="true" />;
      case 1:
        return <Flame className="size-5 text-primary" aria-hidden="true" />;
      case 2:
        return <Wind className="size-5 text-primary" aria-hidden="true" />;
      case 3:
      default:
        return <Hammer className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="our-process" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Spec Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-10 border-b border-border">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            Direct Crew Dispatch • MD / DC / NoVA
          </div>

          <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" />
              24/7 Rapid Mobilization
            </span>
            <span className="hidden sm:inline text-border">|</span>
            <span className="hidden sm:flex items-center gap-1.5">
              <ShieldAlert className="size-3.5 text-secondary" />
              Direct Insurance Coordination
            </span>
          </div>
        </div>

        {/* Section Heading & Intro Paragraph */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-primary font-mono block mb-2">
            Restoration Methodology
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            How we restore your property after a fire
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            Four structured steps bring your property from emergency board-up back to safe, move-in condition.
          </p>
          <p className="text-base text-foreground/80 leading-relaxed">
            Fire and smoke damage compound every hour soot sits on metals and toxic combustion residues penetrate wall cavities. TriServe deploys certified local technicians directly to your site to stabilize the structure, remediate hazardous soot, eliminate deep-set odors, and complete the full rebuild under one accountable contract.
          </p>
        </div>

        {/* 4-Step Process Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {steps.map((item, i) => <motion.div key={item.step} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.32,
          delay: shouldReduceMotion ? 0 : i * 0.06,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col h-full">
              <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/60 transition-colors shadow-xs rounded-lg p-6">
                <div>
                  {/* Step Header with Monospace numbering & Icon */}
                  <div className="flex items-center justify-between pb-4 mb-4 border-b border-border/60">
                    <span className="text-xs font-mono font-bold text-muted-foreground uppercase tracking-widest">
                      Step {item.step}
                    </span>
                    <div className="p-2 rounded-md bg-primary/10 flex items-center justify-center">
                      {getIcon(i)}
                    </div>
                  </div>

                  {/* Card Title & Phase */}
                  <CardHeader className="p-0 mb-3 gap-1">
                    <span className="text-xs font-mono font-medium text-primary uppercase">
                      {item.phase}
                    </span>
                    <CardTitle className="text-xl font-bold text-card-foreground font-default">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  {/* Summary */}
                  <CardDescription className="p-0 text-sm text-muted-foreground leading-normal mb-5">
                    {item.summary}
                  </CardDescription>

                  {/* Operational Detail Points */}
                  <ul className="space-y-2.5 mb-6 text-xs text-foreground/90 font-sans border-t border-border/40 pt-4">
                    {item.bullets.map((bullet, bulletIdx) => <li key={bulletIdx} className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <span>{bullet}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Footer Spec Badge */}
                <div className="mt-auto pt-3 border-t border-border flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span>{item.specs}</span>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Direct Coordination Action Block */}
        <div className="mt-14 rounded-lg bg-muted/60 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="text-lg font-bold text-foreground font-default mb-1">
              Need immediate emergency board-up or soot assessment?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our local crews respond 24/7 across Maryland, DC, and Northern Virginia with zero call center delays.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-5 h-11 shadow-xs w-full sm:w-auto">
              <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call 855-435-6631</span>
              </a>
            </Button>
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground font-medium px-5 h-11 w-full sm:w-auto">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request service online</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}